import ContextVariableAccessor from '@/core/application-dictionary/components/ContextVariableAccessor';
import AccessLevelMixin from '@/core/application-dictionary/mixins/AccessLevelMixin';
import {IADField} from '@/shared/model/ad-field.model';
import {debounce, kebabCase} from 'lodash';
import pluralize from 'pluralize';
import {Component, Mixins, Vue, Watch} from 'vue-property-decorator';

const baseApiColumn: string = 'api/ad-columns';

const AdInputLookupProps = Vue.extend({
  props: {
    classNames: [Array, Object],
    disabled: Boolean,
    fieldMeta: Object,
    items: Array,
    labelFields: Array,
    lookupByField: String,
    placeholder: String,
    query: Array,
    size: String,
    tabId: String,
    tableName: String,
    value: [String, Number],
  },
});

@Component
export default class AdInputLookup extends Mixins(AccessLevelMixin, ContextVariableAccessor, AdInputLookupProps) {

  private validationRule: string = null;

  private apiEndpoint: string = null;

  private isUserTable: boolean = false;

  private isOrgTable: boolean = false;

  private abortController = new AbortController();

  private debounceUpdateRelationships: (value) => void;

  loading: boolean = false;
  key: number | string = null;
  lookupItems: any[] = [];
  identifiers: string[] = ['id'];

  @Watch('value')
  onValueChanged(value?: number) {
    if (this.fieldMeta) {
      if (this.loading) {
        this.abortController.abort();
        this.abortController = new AbortController();
      }
      this.debounceUpdateRelationships(value);
    } else {
      this.key = value;
    }
  }

  onChanged(value?: number) {
    this.$emit('input', value);
    this.$emit('change', value);
  }

  async created() {
    this.debounceUpdateRelationships = debounce(this.initRelationships, 500);
    if (this.fieldMeta) {
      this.initApiEndpoint();
      await this.retrieveIdentifiers();
      await this.initValidationRule();
      this.initRelationships(this.value as number);
    } else if (this.tableName) {
      const resourceName = pluralize.plural(kebabCase(this.tableName));

      this.apiEndpoint = `api/${resourceName}`;
      this.isUserTable = this.tableName === 'ad_user';
      this.isOrgTable = this.tableName === 'ad_organization';

      if (this.labelFields) {
        this.identifiers = this.labelFields as string[];
      } else {
        await this.retrieveIdentifiers();
      }

      this.fetchRecords(this.value);
    }
  }

  /**
   * This method is invoked each time user typing in el-select component
   * with remote attribute enabled.
   * @param query
   */
  fetchRecords(query?: string | number) {
    this.setLoadingIndicator(true);
    let baseQuery: string[] = ['active.equals=true'];
    let criteriaQuery: string[] = [];

    if (this.fieldMeta) {
      // Append additional query from the cached validation rule query, if any.
      if (this.validationRule) {
        baseQuery.push(this.validationRule);
      }
    } else if (this.query) {
      baseQuery = [...baseQuery, ...(this.query as string[])];
    }

    if (!!query) {
      if (typeof query === 'number') {
        baseQuery.push(`id.equals=${query}`);
        criteriaQuery = this.isOrgTable ? baseQuery : this.updateCriteria(baseQuery);
      } else if (this.isUserTable) {
        baseQuery.push(`userLogin.contains=${query}`);
        criteriaQuery = this.updateCriteria(baseQuery);
      } else {
        const fieldName = this.lookupByField || this.identifiers[0];
        baseQuery.push(`${fieldName}.contains=${query}`);
        criteriaQuery = this.updateCriteria(baseQuery, this.isOrgTable);
      }
    } else {
      criteriaQuery = this.updateCriteria(baseQuery, this.isOrgTable);
    }

    this.dynamicWindowService(this.apiEndpoint)
      .retrieve({
        criteriaQuery,
        paginationQuery: {
          page: 0,
          size: 20,
          sort: this.isUserTable ? ['id'] : this.identifiers,
        },
      })
      .then(res => {
        this.lookupItems = res.data;
        this.$emit('update:items', this.lookupItems);
        this.$emit('list-updated', this.lookupItems);
      })
      .finally(() => this.setLoadingIndicator(false));
  }

  private initApiEndpoint() {
    const field = this.fieldMeta as IADField;
    const column = field.adColumn;
    const tableName = column?.foreignKey ? column.importedTable : field.adTable.name;
    const resourceName = pluralize.plural(kebabCase(tableName));
    this.apiEndpoint = `api/${resourceName}`;
    this.isUserTable = tableName === 'ad_user';
    this.isOrgTable = tableName === 'ad_organization';
  }

  private initValidationRule() {
    return new Promise(async resolve => {

      // Parse the validation rule which is used to filter the reference key records.
      const field = this.fieldMeta as IADField;
      const column = field.adColumn;
      const overrideColumn = field.adValidationRule !== void 0 && field.adValidationRule !== null;
      const vRule = overrideColumn ? field.adValidationRule : column?.adValidationRule;

      if (vRule) {
        let referenceFilter = <string>this.getContext({
          defaultTabId: this.tabId,
          text: vRule.query,
        });

        if (referenceFilter?.includes('{select', 0)) {
          this.setLoadingIndicator(true);

          try {
            referenceFilter = await this.parseValidationQuery({
              defaultTabId: this.tabId,
              text: referenceFilter,
            });
          } catch (err) {
            console.warn('Failed initializing validation rule for field', field.name);
          }
        }

        this.setLoadingIndicator(false);
        // Avoid exception when creating a new record, which will invoke
        // the backend API with a bad request.
        if (referenceFilter && !referenceFilter.includes('=null')) {
          this.validationRule = referenceFilter;
        }
      }
      resolve(true);
    });
  }

  private initRelationships(initialValue?: number) {
    this.setLoadingIndicator(true);

    const field = this.fieldMeta as IADField;
    const column = field.adColumn;
    const tableName = column?.foreignKey ? column.importedTable : field.adTable.name;
    const resourceName = pluralize.plural(kebabCase(tableName));
    const api = `api/${resourceName}`;
    const baseQuery: string[] = initialValue ? [`id.equals=${initialValue}`] : [];
    let criteriaQuery: string[] = [];

    // Append additional query from the cached validation rule query, if any.
    if (this.validationRule) {
      baseQuery.push(this.validationRule);
    }

    if (initialValue) {
      if (this.isOrgTable) {
        criteriaQuery = baseQuery;
      } else {
        criteriaQuery = this.updateCriteria(baseQuery);
      }
    } else {
      if (this.isOrgTable) {
        var param = 0;
        criteriaQuery = this.updateCriteria(baseQuery, true);
        criteriaQuery.forEach(query => {
          if (query.includes("id.notEquals=1")) {
            param = param + 1;
          }
          if (query.includes("id.in=1")) {
            param = param + 1;
          }
        })

        if (param >= 2) {
          const index = criteriaQuery.indexOf("id.in=1");
          if (index > -1) {
            criteriaQuery.splice(index, 1);
          }
        }
      } else {
        criteriaQuery = this.updateCriteria(baseQuery);
      }
    }

    this.dynamicWindowService(api)
      .retrieve({criteriaQuery, abortController: this.abortController})
      .then(res => {
        this.lookupItems = res.data;
        this.$nextTick(() => this.key = initialValue);
      })
      .catch(err => {
        if (err.message === 'canceled') {
          // Do nothing if canceled.
        }
      })
      .finally(() => this.setLoadingIndicator(false));
  }

  private retrieveIdentifiers() {
    return new Promise(resolve => {
      if (this.isUserTable) {
        this.identifiers = ['userLogin'];
        resolve(true);
      } else {
        let baseQuery = [];

        if (this.fieldMeta) {
          const field = this.fieldMeta as IADField;
          const column = field.adColumn;
          const table = column?.foreignKey ? column.importedTable : field.adTable.id;

          baseQuery = [`${typeof table === 'string' ? 'adTableName' : 'adTableId'}.equals=${table}`];
        } else if (this.tableName) {
          baseQuery = [`adTableName.equals=${this.tableName}`];
        }

        const criteriaQuery = [
          'active.equals=true',
          'identifier.equals=true',
          ...baseQuery
        ];

        this.dynamicWindowService(baseApiColumn)
          .retrieve({criteriaQuery})
          .then(res => {
            if (res.data.length) {
              this.identifiers = res.data.map(item => item.name);
              resolve(true);
            }
          })
          .catch(err => resolve(false));
      }
    })
  }

  private setLoadingIndicator(value: boolean) {
    this.loading = value;
    this.$emit('loading', value);
  }

  printLabel(data: any) {
    return this.identifiers
      .filter(key => data.hasOwnProperty(key))
      .map(key => data[key])
      .join(' - ');
  }
}
